import { CALENDAR, PROPOSAL_TASK_DETAIL } from '@/constants';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.locale('ja');

const utils = {
    calcMedian(array, key) {
        const sortedArray = this.sortNumber(array, 'asc', key);
        const middleIndex = Math.floor(sortedArray.length / 2);

        // 要素数が偶数の場合2つの数値の平均 10個の場合は5, 6個目の平均値
        // 要素数が奇数の場合は対象の要素の値 9個の場合は5番目の値
        if (sortedArray.length % 2 === 0) {
            if (key) {
                return (
                    (sortedArray[middleIndex - 1][key] +
                        sortedArray[middleIndex][key]) /
                    2
                );
            } else {
                return (
                    (sortedArray[middleIndex - 1] + sortedArray[middleIndex]) /
                    2
                );
            }
        } else {
            if (key) {
                return sortedArray[middleIndex][key];
            } else {
                return sortedArray[middleIndex];
            }
        }
    },

    checkError(errorObj) {
        for (let name in errorObj) {
            if (Array.isArray(errorObj[name])) {
                for (let i = 0; i < errorObj[name].length; i++) {
                    if (errorObj[name][i]) {
                        return true;
                    }
                }
            } else if (errorObj[name]) {
                return true;
            }
        }
    },

    convertDoubleSpaceToSpace(string) {
        if (!string) {
            return;
        }
        return (
            string
                // eslint-disable-next-line
                .replace(/　/g, ' ')
                .replace(/ +/g, ' ')
        );
    },

    convertToHiragana(string) {
        if (!string) {
            return;
        }
        return string.replace(/[\u30a1-\u30f6]/g, s => {
            return String.fromCharCode(s.charCodeAt(0) - 0x60);
        });
    },

    convertToHalfWidth(string) {
        if (!string) {
            return;
        }
        return string.replace(/[Ａ-Ｚａ-ｚ０-９]/g, s => {
            return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
        });
    },

    convertToSearchString(string) {
        if (!string) {
            return;
        }
        return utils.convertDoubleSpaceToSpace(
            utils.convertToHalfWidth(
                utils.convertToHiragana(string.toLowerCase())
            )
        );
    },

    deleteAllSpaces(string) {
        if (!string) {
            return;
        }
        return string.replace(/\s+/g, '');
    },

    downloadFile(data, fileName) {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    },

    downloadUrl(url, fileName) {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    },

    formatDate(date, format) {
        if (!format) {
            return dayjs(date)
                .toISOString()
                .replace(/\.[0-9]+/, '');
        }
        return dayjs(date).format(format);
    },

    getDateResetHours(d) {
        let date = d ? new Date(d) : new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    },

    getDays(day1, day2) {
        let oneDay = 24 * 60 * 60 * 1000;
        let date1 = this.getDateResetHours(day1);
        let date2 = this.getDateResetHours(day2);
        let days = Math.round(
            Math.abs((date1.getTime() - date2.getTime()) / oneDay)
        );
        return days;
    },

    getErrorUsages(args) {
        const error = { message: '' };
        const usagesRest = args.usages
            ? args.usages.max - args.usages.current
            : null;
        const usagesCountRest = args.usagesCount
            ? args.usagesCount.max - args.usagesCount.current
            : null;

        if (args.usagesCount && usagesCountRest <= 0) {
            // 登録回数がオーバーしている場合
            error.message = `月に${args.usagesCount.max}回までしか登録できません。登録回数は毎月1日にリセットされます`;
        } else if (args.usages && usagesRest <= 0) {
            // 登録数がオーバーしている場合
            error.message = `最大${args.usages.max}個までしか登録できません。登録するには${args.target}を削除してください`;
        } else if (args.count) {
            if (
                (args.usagesCount && usagesCountRest < args.count) ||
                (args.usages && usagesRest < args.count)
            ) {
                // 登録できる数が少ない方のエラーメッセージを表示する
                if (args.usagesCount && usagesCountRest <= usagesRest) {
                    // 登録回数はオーバーしていないが、複数選択したもの全ては登録できない場合
                    error.message = `残り${usagesCountRest}回までしか登録できません`;
                    error.rest = usagesCountRest;
                } else if (args.usages) {
                    // 登録数はオーバーしていないが、複数選択したもの全ては登録できない場合
                    error.message = `残り${usagesRest}個までしか登録できません`;
                    error.rest = usagesRest;
                }
            }
        }
        return error;
    },

    getGAPeriod(days = CALENDAR.ga.days) {
        // GAのデフォルト期間: 30日前～1日前(3ヶ月前)
        let from;
        let to = new Date(CALENDAR.ga.maxDate);

        if (days === 'all') {
            from = new Date(CALENDAR.ga.minDate);
        } else {
            from = new Date(CALENDAR.ga.maxDate);
            from.setDate(from.getDate() - days + 1);
        }
        return [from, to];
    },

    getGSCPeriod(days = CALENDAR.gsc.days) {
        // サチコのデフォルト期間: 30日前～3日前
        let from;
        let to = new Date(CALENDAR.gsc.maxDate);

        if (days === 'all') {
            from = new Date(CALENDAR.gsc.minDate);
        } else {
            from = new Date(CALENDAR.gsc.maxDate);
            from.setDate(from.getDate() - days + 1);
        }
        return [from, to];
    },

    getSiteImportantKeywordPeriod(days = CALENDAR.siteImportantKeyword.days) {
        // サチコのデフォルト期間: 91日前～3日前
        let from;
        let to = new Date(CALENDAR.siteImportantKeyword.maxDate);

        if (days === 'all') {
            from = new Date(CALENDAR.siteImportantKeyword.minDate);
        } else {
            from = new Date(CALENDAR.siteImportantKeyword.maxDate);
            from.setDate(from.getDate() - days + 1);
        }
        return [from, to];
    },

    getGADemoPeriod(days = CALENDAR.demoGa.days) {
        // デモサイト用のGAの期間
        let from;
        let to = new Date(CALENDAR.demoGa.maxDate);

        if (days === 'all') {
            from = new Date(CALENDAR.demoGa.minDate);
        } else {
            from = new Date(CALENDAR.demoGa.maxDate);
            from.setDate(from.getDate() - days + 1);
        }
        return [from, to];
    },

    getGSCDemoPeriod(days = CALENDAR.demoGsc.days) {
        // デモサイト用のGSCの期間
        let from;
        let to = new Date(CALENDAR.demoGsc.maxDate);

        if (days === 'all') {
            from = new Date(CALENDAR.demoGsc.minDate);
        } else {
            from = new Date(CALENDAR.demoGsc.maxDate);
            from.setDate(from.getDate() - days + 1);
        }
        return [from, to];
    },
    getSiteImportantKeywordDemoPeriod(
        days = CALENDAR.demoSiteImportantKeyword.days
    ) {
        // サチコのデフォルト期間: 91日前～3日前
        let from;
        let to = new Date(CALENDAR.demoSiteImportantKeyword.maxDate);

        if (days === 'all') {
            from = new Date(CALENDAR.demoSiteImportantKeyword.minDate);
        } else {
            from = new Date(CALENDAR.demoSiteImportantKeyword.maxDate);
            from.setDate(from.getDate() - days + 1);
        }
        return [from, to];
    },

    getFavicon(url) {
        if (!url) {
            return;
        }
        let domain = url.replace(/^https?:\/{2,}/, '');
        domain = url.replace(/^sc-domain:/, '');
        return '//www.google.com/s2/favicons?domain=' + domain;
    },

    getFlattenHeadings(headerStructures) {
        if (!headerStructures) return [];
        const headings = [];
        const regexp = /^alt="(.*)"$/;

        headerStructures.forEach(item => {
            // nullの場合、空文字に変換
            item.text = item.text || '';
            // 画像である && テキストがalt=""で囲まれている場合、中のテキストのみを抽出する
            const text =
                item.isImage && regexp.test(item.text)
                    ? item.text.match(regexp)[1].trim()
                    : item.text.trim();

            headings.push({ type: item.type, text });

            if (item.children && item.children.length) {
                headings.push(...this.getFlattenHeadings(item.children));
            }
        });
        return headings;
    },

    getStartDate(date, unit = 'day') {
        if (unit === 'days') {
            unit = 'day';
        }
        return dayjs(date).startOf(unit).$d;
    },
    getEndDate(date, unit = 'day') {
        if (unit === 'days') {
            unit = 'day';
        }
        // グラフの都合上、時間は00:00にする
        return dayjs(date)
            .endOf(unit)
            .startOf('date').$d;
    },
    // dateがstartとendの期間内かどうかをbooleanで返すよ
    // start <= date <= end という仕様でございますのでご注意を。
    isBetween(date, start, end) {
        const d = dayjs(date);
        return d.isBetween(start, end, null, []);
    },

    getProposalTaskDetail(proposal) {
        let taskDetail = '';
        if (
            proposal.type === 'changeTd' ||
            proposal.type === 'rewrite' ||
            proposal.type === 'createContent'
        ) {
            taskDetail = PROPOSAL_TASK_DETAIL[proposal.type].replace(
                /{{keyword}}/g,
                proposal.keyword
            );
        } else if (proposal.type === 'seoCheckUp') {
            taskDetail = PROPOSAL_TASK_DETAIL[proposal.type][proposal.content];
        }

        if (proposal.type === 'createContent') {
            return taskDetail;
        }

        if (taskDetail && proposal.url) {
            taskDetail += '\n【対象URL】 ' + proposal.url;
        }
        return taskDetail;
    },

    getTmpKey() {
        return String(
            Math.floor(Math.random() * (10000000000 - 1000000000)) + 1000000000
        );
    },

    // グラフに表示する目盛りの間隔と個数を返す。
    // [0, 200, 400, 600, 800] のように、目盛りとして表示される数値を全て含む配列を返す。
    getTickValues(ticks, max, unit) {
        // ticks: グラフのY軸に表示する目盛りの数。3項目以上グラフを同時に表示すると、そもそもグラフに目盛りが表示されない仕様のため注意。
        // max: グラフデータの最大値を与える。整数で丸めたりして、最大値をギリギリ超える形で、適切にY軸の天井を決めてくれる。
        // unit: 目盛りを「ooの倍数」にしたい場合に指定する。例えば、目盛りに表示する数字は10の倍数じゃないと気持ち悪いなどあれば10。
        // 指定しない場合は、1桁目を数字を四捨五入していい感じにしてくれるので、特に理由なければ指定する必要はないかも。
        let values = [];
        if (max) {
            let step = Math.ceil(max / (ticks - 1));
            // 任意の倍数に丸める
            if (unit) {
                step = Math.ceil(step / unit) * unit;
            } else {
                // stepの1桁目以降の数字を丸める
                let digit = Math.pow(10, String(step).length - 1);
                step = Math.ceil(step / digit) * digit;
            }
            for (let i = 0; i < ticks; i++) {
                values.push(step * i);
            }
        }
        return values;
    },

    getTimeType(days, width) {
        if (days > 120) {
            return 'timeMonth';
        } else if (days > 31) {
            return 'timeWeek';
        } else if (width && width / days < 30) {
            // グラフの幅によってはX軸のラベル文字が重なるので
            return 'timeWeek';
        }
        return 'timeDay';
    },

    getTranslateValue(transform) {
        let array = transform
            .substring(transform.indexOf('(') + 1, transform.indexOf(')'))
            .split(/[, ]/);

        for (let i = 0; i < array.length; i++) {
            array[i] = Number(array[i]);
        }
        return array;
    },

    isEmpty(string) {
        if (!string && string !== 0) {
            return true;
        }
        return false;
    },

    isSameDay(string1, string2) {
        if (!string1 || !string2) {
            return false;
        }
        return dayjs(string1).isSame(string2, 'day');
    },

    isScDomain(string) {
        if (string && /^sc-domain/.test(string)) {
            return true;
        }
        return false;
    },

    // 指定した桁数分の小数点を残しつつ数字を四捨五入 digitがない場合は小数第1位まで残して小数第2位を四捨五入
    roundFloatingPointNumber(number, digit) {
        if (!number && number !== 0) {
            return number;
        }
        const pow = digit ? Math.pow(10, digit) : 10;
        return Math.round(number * pow) / pow;
    },

    // 指定した桁数分の小数点を残しつつ数字を切り捨てる digitがない場合は小数第1位まで残して小数第2位を切り捨て
    roundDownFloatingPointNumber(number, digit) {
        if (!number && number !== 0) {
            return number;
        }
        const pow = digit ? Math.pow(10, digit) : 10;
        return Math.floor(number * pow) / pow;
    },

    sort(array, type, orderby, key) {
        if (type === 'date') {
            return this.sortDate(array, orderby, key);
        } else if (type === 'number') {
            return this.sortNumber(array, orderby, key);
        } else {
            return this.sortString(array, orderby, key);
        }
    },

    sortDate(array, orderby = 'asc', key) {
        return array.sort((a, b) => {
            let date1 = key ? a[key] : a;
            let date2 = key ? b[key] : b;
            if (date1 === date2) {
                return 0;
            } else if (!date1) {
                return 1;
            } else if (!date2) {
                return -1;
            }
            let time1 = utils.getDateResetHours(date1).getTime();
            let time2 = utils.getDateResetHours(date2).getTime();
            if (orderby === 'asc') {
                return time1 - time2;
            } else {
                return time2 - time1;
            }
        });
    },

    sortNumber(array, orderby = 'asc', key) {
        return array.sort((a, b) => {
            let num1 = key ? a[key] : a;
            let num2 = key ? b[key] : b;
            if (num1 === num2) {
                return 0;
            } else if (num1 === null) {
                return 1;
            } else if (num2 === null) {
                return -1;
            }
            if (orderby === 'asc') {
                return num1 - num2;
            } else {
                return num2 - num1;
            }
        });
    },

    sortString(array, orderby = 'asc', key) {
        return array.sort((a, b) => {
            let str1 = utils.convertToSearchString(key ? a[key] : a);
            let str2 = utils.convertToSearchString(key ? b[key] : b);
            if (str1 === str2) {
                return 0;
            } else if (!str1) {
                return 1;
            } else if (!str2) {
                return -1;
            }
            if (str1 > str2) {
                if (orderby === 'asc') {
                    return 1;
                } else {
                    return -1;
                }
            } else if (str1 < str2) {
                if (orderby === 'asc') {
                    return -1;
                } else {
                    return 1;
                }
            }
        });
    },

    isAfterDate(date1, date2, unit = 'day') {
        return dayjs(date1).isAfter(date2, unit);
    },
    isBeforeDate(date1, date2, unit = 'day') {
        return dayjs(date1).isBefore(date2, unit);
    },
    isSameOrAfterDate(date1, date2, unit = 'day') {
        return dayjs(date1).isSameOrAfter(date2, unit);
    },

    isPdf(evaluate) {
        const parsed = new URL(evaluate);
        return parsed.pathname.endsWith('.pdf');
    },

    // タスクの絞り込み条件に関するメソッド
    // 2箇所で使うためこちらに記載
    isMatched(item, filter) {
        const operatorUserId = filter.operatorUserId;
        const type = filter.type;
        const keyword = filter.keyword;
        const url = filter.url;
        const executionDateUnit = filter.executionDateUnit;
        const typeDoneDateUnit = filter.typeDoneDateUnit;

        if (operatorUserId) {
            if (operatorUserId === 'free') {
                if (item.operatorUserId) {
                    return false;
                }
            } else if (item.operatorUserId !== operatorUserId) {
                return false;
            }
        }
        if (type && item.type !== type) {
            return false;
        }
        if (keyword && item.keyword !== keyword) {
            return false;
        }
        if (url && item.url !== url) {
            return false;
        }
        if (executionDateUnit) {
            // 施策完了予定日がないタスクの場合、「すべて」以外はfalseを返したい
            if (!item.executionDate) {
                return false;
            }
            // 施策完了予定日が「期限切れ」のとき、完了済みのタスクは除外
            if (executionDateUnit === 'expired' && item.column === 'done') {
                return false;
            }
            if (!this.isMatchedDate(item.executionDate, executionDateUnit)) {
                return false;
            }
        }
        if (typeDoneDateUnit) {
            // 施策完了日がないタスクの場合、「すべて」以外はfalseを返したい
            if (!item.typeDoneDate) {
                return false;
            }
            if (!this.isMatchedDate(item.typeDoneDate, typeDoneDateUnit)) {
                return false;
            }
        }
        return true;
    },

    isMatchedDate(dateString, unit) {
        const date = new Date(dateString);
        const now = new Date();

        let start = null;
        let end = null;

        if (unit === 'expired') {
            return (
                utils.getDateResetHours(date).getTime() <
                utils.getDateResetHours(now).getTime()
            );
        }

        if (unit === 'day') {
            start = now;
            end = now;
        }

        if (unit === 'week') {
            start = utils.getStartDate(now, 'week');
            end = utils.getEndDate(now, 'week');
        }

        if (unit === 'month') {
            start = utils.getStartDate(now, 'month');
            end = utils.getEndDate(now, 'month');
        }

        if (unit === 'lastWeek') {
            const lastWeek = dayjs(now).subtract(1, 'week');
            start = utils.getStartDate(lastWeek, 'week');
            end = utils.getEndDate(lastWeek, 'week');
        }

        if (unit === 'nextWeek') {
            const nextWeek = dayjs(now).add(1, 'week');
            start = utils.getStartDate(nextWeek, 'week');
            end = utils.getEndDate(nextWeek, 'week');
        }

        if (unit === 'lastMonth') {
            const lastMonth = dayjs(now).subtract(1, 'month');
            start = utils.getStartDate(lastMonth, 'month');
            end = utils.getEndDate(lastMonth, 'month');
        }

        if (unit === 'threeMonths') {
            start = dayjs(now)
                .subtract(3, 'month')
                .add(1, 'day');
            end = now;
        }
        if (unit === 'sixMonths') {
            start = dayjs(now)
                .subtract(6, 'month')
                .add(1, 'day');
            end = now;
        }

        // タイムゾーンや時刻による比較漏れを防ぐため、日付のみの文字列で比較
        const startDateString = utils.formatDate(start, 'YYYY-MM-DD');
        const endDateString = utils.formatDate(end, 'YYYY-MM-DD');
        // 作成されたばかりのタスクは、Date型で日付が入ってくる場合もあるので、念のため明示的に文字列にする
        const valueDateString = utils.formatDate(date, 'YYYY-MM-DD');

        return utils.isBetween(valueDateString, startDateString, endDateString);
    }
};

export default utils;
